import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import FormNumberInput from '../../../components/form/FormNumberInput';
import { companyAdd, companyUpdate } from '../../../store/slices/adminSlices/companySlice';
import { companyRoleOptionsGet } from '../../../store/slices/adminSlices/companyRoleSlice';
import { timezoneOptionGet } from '../../../store/slices/adminSlices/timezoneSlice';

const AddEditCompanyModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [companyRoleOptions, setCompanyRoleOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  const initialValues = {
    username: '',
    password: '',
    name: '',
    company_domain: '',
    active: 'true',
    concurrent: 0,
    companyRoleId: '',
    permitted_users: 0,
    time_zone_id: ''
  };

  const validationSchema = Yup.object({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
    name: Yup.string().required('Name is required'),
    company_domain: Yup.string().required('Domain is required'),
    concurrent: Yup.string().required('concurrent is required'),
    companyRoleId: Yup.string().required('company role is required'),
    time_zone_id: Yup.string().required('time zone is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(companyUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(companyAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      setValues({
        username: editRecord?.username || '',
        password: editRecord?.password || '',
        name: editRecord?.name || '',
        company_domain: editRecord?.company_domain || '',
        concurrent: editRecord?.concurrent || 0,
        permitted_users: editRecord?.permitted_users || 0,
        active: String(editRecord?.active) || 'false',
        companyRoleId: editRecord?.companyRoleId || '',
        time_zone_id: editRecord?.time_zone_id || ''
      });
    }
  }, [editRecord]);

  const getCompanyRoleOptions = async () => {
    try {
      const response = await dispatch(companyRoleOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setCompanyRoleOptions(options);
      } else {
        setCompanyRoleOptions([]);
      }
    } catch (error) {
      setCompanyRoleOptions([]);
      console.log('error', error);
    }
  };

  const getTimezoneOptions = async () => {
    try {
      const response = await dispatch(timezoneOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.zone_name,
          value: val.id
        }));
        setTimezoneOptions(options);
      } else {
        setTimezoneOptions([]);
      }
    } catch (error) {
      setTimezoneOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getTimezoneOptions();
      getCompanyRoleOptions();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Company' : 'Add Company'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="username"
                label="Username"
                placeholder="Enter Username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="password"
                label="Password"
                placeholder="Enter Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="company_domain"
                label="Company Domain"
                placeholder="Enter Company Domain"
                value={values.company_domain}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={values.active}
                onChange={(e) => setFieldValue('active', e)}
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="concurrent"
                label="Concurrent"
                placeholder="Enter Concurrent"
                value={values.concurrent}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="permitted_users"
                label="Permitted Users"
                placeholder="Enter Permitted Users"
                value={values.permitted_users}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="companyRoleId"
                label="Company Role"
                placeholder="Select Company Role"
                value={values.companyRoleId}
                options={companyRoleOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="time_zone_id"
                label="Timezone"
                placeholder="Select Timezone"
                value={values.time_zone_id}
                options={timezoneOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditCompanyModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditCompanyModal;
